import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL as CONFIG_API_URL } from '../config';
const API_URL = CONFIG_API_URL || 'http://8.141.122.155:3002/api';

import { 
  FileText, Download, TrendingUp, AlertTriangle, 
  BarChart2, Target, Calendar, ChevronDown, Check,
  Shield, Layers, Activity
} from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  RadialLinearScale,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line, Radar, Doughnut, Bar } from 'react-chartjs-2';
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm';
import html2canvas from 'html2canvas';

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement, BarElement,
  RadialLinearScale, ArcElement, Title, Tooltip, Legend, Filler
);

export default function Reports() {
  const [isGenerating, setIsGenerating] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [timeRange, setTimeRange] = useState('最近 30 天');
  const [customDate, setCustomDate] = useState({ start: '', end: '' });
  
  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const pid = localStorage.getItem('globalProject');
      const bid = localStorage.getItem('globalBrand');
      
      const res = await axios.get(`${API_URL}/dashboard/insights`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          geoTaskId: (pid === 'all' || pid === 'undefined' || !pid) ? undefined : pid,
          brandProjectId: (bid === 'all' || bid === 'undefined' || !bid) ? undefined : bid
        }
      });
      
      if (res.data.success && res.data.data) {
        setDashboardData(res.data.data);
      }
      
      // Fetch some real negative/neutral alerts
      const resAlerts = await axios.get(`${API_URL}/results`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          brandProjectId: (bid === 'all' || bid === 'undefined' || !bid) ? undefined : bid,
          sentiment: 'negative',
          limit: 3
        }
      });
      
      if (resAlerts.data.success && resAlerts.data.data) {
        setAlerts(resAlerts.data.data);
      }
      
    } catch (e) {
      console.error("Failed to load report data", e);
    }
  };

  useEffect(() => {
    fetchData();
    window.addEventListener('globalFilterChange', fetchData);
    return () => window.removeEventListener('globalFilterChange', fetchData);
  }, []);

    const handleExportImage = async () => {
    const reportElement = document.getElementById('report-content');
    if (!reportElement) return;

    try {
      const canvas = await html2canvas(reportElement, {
        scale: 2, // Higher quality
        useCORS: true,
        backgroundColor: document.documentElement.classList.contains('dark') ? '#0f172a' : '#ffffff'
      });
      
      const image = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = image;
      link.download = `对比分析报告_${new Date().toISOString().slice(0, 10)}.png`;
      link.click();
    } catch (error) {
      console.error('Failed to export image', error);
    }
  };

  const handleGenerate = () => {
    setIsGenerating(true);
    setTimeout(() => {
      setIsGenerating(false);
      setShowReport(true);
    }, 800);
  };

  // Derive data from dashboardData
  const radarData = dashboardData?.radarData || { labels: [], datasets: [] };
  const sovPieData = dashboardData?.sovData || { labels: [], datasets: [] };
  const sentimentBarData = dashboardData?.sentimentData || { labels: [], datasets: [] };
  const competitorTable = dashboardData?.competitorTable || [];
  
  const mainBrandInfo = competitorTable.find(c => c.isSelf) || { brand: '主品牌', sov: '0%' };
  const competitors = competitorTable.filter(c => !c.isSelf);
  const topCompetitor = competitors.length > 0 ? competitors[0] : { brand: '暂无竞品' };

  // Use real SOV trend from backend
  const colors = dashboardData?.sovData?.datasets?.[0]?.backgroundColor || ['#2996cf', '#f43f5e', '#8b5cf6'];
  const realSovTrend = dashboardData?.realSovTrend || { labels: [], datasets: [] };
  
  const lineDatasets = realSovTrend.datasets.map((dataset, idx) => {
    const isMain = dataset.label === mainBrandInfo.brand;
    return {
      label: dataset.label,
      data: dataset.data,
      borderColor: isMain ? '#2996cf' : colors[idx % colors.length],
      backgroundColor: isMain ? 'rgba(41, 150, 207, 0.1)' : 'transparent',
      borderWidth: isMain ? 3 : 2,
      borderDash: isMain ? [] : [5, 5],
      tension: 0.4,
      fill: isMain
    };
  });

  const sovLineData = {
    labels: realSovTrend.labels.length > 0 ? realSovTrend.labels : ['暂无数据'],
    datasets: lineDatasets
  };

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top', labels: { usePointStyle: true, boxWidth: 8 } },
      tooltip: {
        callbacks: {
          label: function(context) {
            return context.dataset.label + ': ' + context.parsed.y + '%';
          }
        }
      }
    },
    scales: {
      y: { 
        beginAtZero: true, 
        grid: { color: 'rgba(0,0,0,0.05)' },
        ticks: {
          callback: function(value) { return value + '%'; }
        }
      },
      x: { grid: { display: false } }
    }
  };

  
    const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '65%',
    plugins: {
      legend: { position: 'right', labels: { usePointStyle: true, boxWidth: 8 } },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.parsed || 0;
            const dataset = context.dataset.data;
            const total = dataset.reduce((a, b) => a + b, 0);
            const percentage = total > 0 ? Math.round((value / total) * 100) + '%' : '0%';
            return `${label}: ${value} (${percentage})`;
          }
        }
      }
    }
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top', labels: { usePointStyle: true, boxWidth: 8 } }
    },
    scales: {
      x: { 
        stacked: true,
        grid: { display: false }
      },
      y: { 
        stacked: true, 
        beginAtZero: true,
        grid: { display: false },
        border: { display: false }
      }
    }
  };

  const radarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      r: {
        angleLines: { color: 'rgba(0, 0, 0, 0.05)' },
        grid: { color: 'rgba(0, 0, 0, 0.05)' },
        pointLabels: { font: { size: 12 }, color: '#64748b' },
        ticks: { display: false, min: 0, max: 100 }
      }
    },
    plugins: {
      legend: { position: 'bottom', labels: { usePointStyle: true } },
      tooltip: {
        callbacks: {
          label: function(context) {
            return context.dataset.label + ': ' + context.parsed.r + '%';
          }
        }
      }
    }
  };

  return (
    <div className="p-8 max-w-[1600px] mx-auto space-y-8 pb-20">
      
      {/* Header & Generator Bar */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-800 p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
              <FileText className="text-[#2996cf] w-6 h-6" />
              对比分析报告
            </h1>
            <p className="text-sm text-slate-500 mt-1">一键生成多维度 AI 引擎品牌表现战报，支持导出汇报文档。</p>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
              <div className="relative flex items-center">
                <Calendar className="w-4 h-4 absolute left-3 text-gray-500" />
                <select 
                  className="pl-9 pr-8 py-2 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-slate-600 dark:text-slate-300 text-sm font-medium hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors appearance-none focus:outline-none focus:ring-2 focus:ring-[#2996cf]/50"
                  value={timeRange}
                  onChange={(e) => setTimeRange(e.target.value)}
                >
                  <option value="最近 7 天">最近 7 天</option>
                  <option value="最近 30 天">最近 30 天</option>
                  <option value="最近 90 天">最近 90 天</option>
                  <option value="全部时间">全部时间</option>
                  <option value="自定义范围">自定义范围...</option>
                </select>
                <ChevronDown className="w-4 h-4 absolute right-3 text-gray-500 pointer-events-none" />
              </div>
              {timeRange === '自定义范围' && (
                 <div className="flex items-center space-x-2">
                   <input type="date" className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg px-2 py-1.5 text-sm text-gray-700 dark:text-slate-300 focus:outline-none focus:ring-2 focus:ring-[#2996cf]/50" value={customDate.start} onChange={e => setCustomDate({...customDate, start: e.target.value})} />
                   <span className="text-gray-500 text-sm">-</span>
                   <input type="date" className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg px-2 py-1.5 text-sm text-gray-700 dark:text-slate-300 focus:outline-none focus:ring-2 focus:ring-[#2996cf]/50" value={customDate.end} onChange={e => setCustomDate({...customDate, end: e.target.value})} />
                 </div>
              )}
            </div>
            <button 
              onClick={handleGenerate}
              disabled={isGenerating || !dashboardData}
              className={`flex items-center gap-2 px-6 py-2 rounded-lg transition-colors font-medium shadow-sm ${!dashboardData ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-[#2996cf] text-white hover:bg-blue-600'}`}
            >
              {isGenerating ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  生成中...
                </div>
              ) : (
                <>
                  <BarChart2 className="w-4 h-4" />
                  生成报告
                </>
              )}
            </button>
          </div>
        </div>

        {/* Selected Conditions */}
        <div className="flex flex-wrap items-center gap-2 pt-4 border-t border-gray-100 dark:border-slate-800">
          <span className="text-sm text-slate-400 mr-2">分析对象:</span>
          <div className="flex items-center gap-1.5 px-3 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 rounded-full text-xs font-medium border border-blue-100 dark:border-blue-800/30">
            <Check className="w-3 h-3" />
            主品牌: {mainBrandInfo.brand}
          </div>
          {competitors.length > 0 && <span className="text-slate-300 px-1">VS</span>}
          {competitors.map((c, i) => (
             <div key={i} className="flex items-center gap-1.5 px-3 py-1 bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-full text-xs font-medium border border-gray-200 dark:border-slate-700">
               竞品: {c.brand}
             </div>
          ))}
        </div>
      </div>

      {/* Report Content - Conditionally Rendered */}
      {showReport ? (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
          
          {/* Toolbar */}
          <div className="flex justify-end gap-3">
             
             <button className="flex items-center gap-2 px-4 py-2 bg-[#2996cf] text-white rounded-lg shadow-sm hover:bg-blue-600 transition-colors text-sm font-medium" onClick={handleExportImage}>
               <Download className="w-4 h-4" />
               导出图片
             </button>
          </div>

          <div id="report-content" className="space-y-8 bg-transparent p-1">
          {/* Module A: Executive Summary */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-800 p-8 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-1 h-full bg-[#2996cf]"></div>
            <h2 className="text-lg font-bold text-slate-800 dark:text-white flex items-center gap-2 mb-4">
              <Activity className="w-5 h-5 text-[#2996cf]" />
              AI 执行摘要 (Executive Summary)
            </h2>
            <div className="prose prose-slate dark:prose-invert max-w-none text-slate-600 leading-relaxed">
              <p>
                在过去分析周期内，<strong>{mainBrandInfo.brand}</strong>在各主流 AI 引擎的整体 SOV（声量份额）达到了 <strong>{mainBrandInfo.sov}</strong>。
                作为对比，核心竞品 <strong>{topCompetitor.brand}</strong> 的声量份额为 {topCompetitor.sov}。
                在各大能力维度中，{mainBrandInfo.brand}的推荐倾向主要集中在 <strong>{mainBrandInfo.pros && mainBrandInfo.pros.length > 0 ? mainBrandInfo.pros.join('、') : '多项综合能力'}</strong>。
              </p>
              <p>
                <strong>预警提示</strong>：系统捕捉到{alerts.length}条关于 <strong>{mainBrandInfo.cons && mainBrandInfo.cons.length > 0 ? mainBrandInfo.cons[0] : '潜在缺陷'}</strong> 等方面的负向评价。
                竞品 <strong>{topCompetitor.brand}</strong> 在 <strong>{topCompetitor.pros && topCompetitor.pros.length > 0 ? topCompetitor.pros[0] : '部分场景'}</strong> 表现较好，建议加强针对该细分维度的内容铺设与公关优化。
              </p>
            </div>
          </div>

          {/* Data Modules Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            
            {/* Module B: SOV Trend */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-800 p-6">
               <div className="flex items-center justify-between mb-6">
                 <h2 className="text-lg font-bold text-slate-800 dark:text-white flex items-center gap-2">
                   <TrendingUp className="w-5 h-5 text-indigo-500" />
                   SOV 份额攻防走势
                 </h2>
               </div>
               <div className="h-[300px] w-full">
                 <Line data={sovLineData} options={lineOptions} />
               </div>
            </div>

            {/* Module C: SWOT Radar */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-800 p-6">
               <div className="flex items-center justify-between mb-6">
                 <h2 className="text-lg font-bold text-slate-800 dark:text-white flex items-center gap-2">
                   <Target className="w-5 h-5 text-emerald-500" />
                   核心优劣势雷达对抗
                 </h2>
               </div>
               <div className="h-[300px] w-full flex items-center justify-center">
                 <div className="w-[85%] h-full">
                   <Radar data={radarData} options={radarOptions} />
                 </div>
               </div>
            </div>

            {/* Module: SOV Pie */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-800 p-6">
               <div className="flex items-center justify-between mb-6">
                 <h2 className="text-lg font-bold text-slate-800 dark:text-white flex items-center gap-2">
                   <Activity className="w-5 h-5 text-blue-500" />
                   SOV 提及率
                 </h2>
               </div>
               <div className="h-[300px] w-full flex items-center justify-center">
                 <div className="w-[85%] h-full">
                   <Doughnut data={sovPieData} options={doughnutOptions} />
                 </div>
               </div>
            </div>

            {/* Module: Sentiment Bar */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-800 p-6">
               <div className="flex items-center justify-between mb-6">
                 <h2 className="text-lg font-bold text-slate-800 dark:text-white flex items-center gap-2">
                   <BarChart2 className="w-5 h-5 text-cyan-500" />
                   情感倾向对比
                 </h2>
               </div>
               <div className="h-[300px] w-full">
                 <Bar data={sentimentBarData} options={barOptions} />
               </div>
            </div>

          </div>

          {/* Module D: Risk Alerts */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-800 p-6">
            <h2 className="text-lg font-bold text-slate-800 dark:text-white flex items-center gap-2 mb-6">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              典型负面 / 暴雷预警清单
            </h2>
            
            <div className="space-y-4">
              {alerts.length > 0 ? alerts.map((alert, idx) => (
                <div key={idx} className="flex gap-4 p-4 rounded-xl border border-amber-100 bg-amber-50/30 dark:bg-amber-900/10 dark:border-amber-900/30">
                  <div className="mt-1">
                    <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center text-amber-600">
                      <Shield className="w-4 h-4" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="font-semibold text-slate-800 dark:text-slate-200">{alert.engineName} 输出负面评价</h4>
                      <span className="text-xs text-slate-400">{new Date(alert.createdAt).toLocaleString()}</span>
                    </div>
                    <div className="text-sm text-slate-600 dark:text-slate-400 mb-2 line-clamp-3 prose prose-sm dark:prose-invert">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{alert.answer ? alert.answer.replace(/\>\s*.*商品卡片.*(?:(?:\r?\n)\>.*)*/g, '').trim() : ''}</ReactMarkdown>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium px-2 py-0.5 bg-red-50 text-red-600 rounded whitespace-nowrap flex-shrink-0">需防守</span>
                      <span className="text-xs text-slate-400">指令: {alert.question}</span>
                    </div>
                  </div>
                </div>
              )) : (
                <div className="text-slate-500 text-sm py-4">当前分析周期内未抓取到明显的负面预警信息。</div>
              )}
            </div>

          </div>
          </div>

        </div>
      ) : (
        <div className="flex flex-col items-center justify-center min-h-[40vh] text-center p-8 border-2 border-dashed border-gray-200 dark:border-slate-800 rounded-2xl bg-slate-50/50 dark:bg-slate-900/20">
          <FileText className="w-16 h-16 text-slate-300 dark:text-slate-700 mb-4" />
          <h3 className="text-lg font-medium text-slate-600 dark:text-slate-400 mb-2">报告尚未生成</h3>
          <p className="text-sm text-slate-500 max-w-sm">请在上方选择分析时间范围与竞品对象，点击“生成报告”查看完整的多维度战报。</p>
        </div>
      )}

    </div>
  );
}
